/**
 * List Table Components - Column headers and sort indicators
 */

import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@loader/ui';

import type { SortBy, SortDir } from './types';
import { useColumnResize } from './useColumnResize';

/**
 * Sort indicator component
 */
export function SortIndicator({
  active,
  direction,
}: {
  active: boolean;
  direction: SortDir;
}) {
  if (!active) return null;
  return direction === 'asc' ? (
    <ChevronUp className="ml-1 inline size-3" />
  ) : (
    <ChevronDown className="ml-1 inline size-3" />
  );
}

/**
 * Column header component with resize support
 */
export function ColumnHeader({
  label,
  sortKey,
  currentSortBy,
  currentSortDir,
  onSort,
  className,
  width,
  onResize,
  resizable = true,
}: {
  label: string;
  sortKey: SortBy;
  currentSortBy: SortBy;
  currentSortDir: SortDir;
  onSort: (key: SortBy) => void;
  className?: string;
  width?: number;
  onResize?: (width: number) => void;
  resizable?: boolean;
}) {
  const isActive = currentSortBy === sortKey;

  const { dragWidth, handleProps } = useColumnResize({
    onResize: onResize || (() => {}),
    initialWidth: width || 100,
    minWidth: 80,
  });

  const displayWidth = dragWidth ?? width;

  return (
    <th
      className={cn(
        'px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground select-none relative',
        isActive && 'text-foreground',
        className
      )}
      onClick={() => onSort(sortKey)}
      style={{ width: displayWidth ? `${displayWidth}px` : undefined }}
    >
      <div className="flex items-center">
        {label}
        <SortIndicator active={isActive} direction={currentSortDir} />
      </div>
      {resizable && onResize && (
        <div
          className="absolute top-0 right-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/20 group"
          onClick={(e) => e.stopPropagation()}
          {...handleProps}
        >
          <div className="absolute top-0 right-0 bottom-0 w-px bg-border group-hover:bg-primary" />
        </div>
      )}
    </th>
  );
}
